import { useState, useRef } from 'react';

import { motion, AnimatePresence, useInView } from 'framer-motion';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface SecurityMeasure {
  id: string;
  title: string;
  icon: string;
  description: string;
  details: string[];
  badge?: string;
}

const securityMeasures: SecurityMeasure[] = [
  {
    id: 'compliance',
    title: 'Industry Certifications',
    icon: 'ri-award-line',
    description: 'Verified and audited security standards trusted by enterprise clients worldwide.',
    details: [
      'ISO 27001 certified information security management',
      'SOC 2 Type II audit compliance on data handling',
      'HIPAA readiness for healthcare client engagements',
      'GDPR-compliant data processing for EU clients',
      'Annual third-party penetration testing',
    ],
    badge: 'SOC 2 + ISO 27001',
  },
  {
    id: 'data',
    title: 'Data Protection',
    icon: 'ri-lock-line',
    description: 'Multi-layered controls to ensure your intellectual property never leaves your control.',
    details: [
      'End-to-end encrypted communication channels (TLS 1.3)',
      'Client-dedicated VPNs and isolated network access',
      'Code access limited to project-specific repositories',
      'Automatic data deletion post-engagement per policy',
      'No sub-contracting without explicit client consent',
    ],
    badge: 'AES-256',
  },
  {
    id: 'vetting',
    title: 'Talent Vetting',
    icon: 'ri-shield-user-line',
    description: 'Every engineer undergoes a rigorous 4-stage background and skill verification process.',
    details: [
      'Criminal background check and identity verification',
      'Reference checks with 2+ previous employers',
      'NDA and IP assignment agreement signed pre-start',
      'Secure device provisioning with MDM policies',
      'Ongoing security awareness training (quarterly)',
    ],
    badge: '4-Stage',
  },
  {
    id: 'ip',
    title: 'IP & Ownership',
    icon: 'ri-copyright-line',
    description: 'You own 100% of the work produced. No exceptions, no hidden clauses, no retainers.',
    details: [
      'Work-for-hire clause in every engagement contract',
      'Full IP transfer on project completion or termination',
      'Client owns all source code, designs, and documentation',
      'No vendor lock-in — walk away with everything anytime',
      'Audit trail of all deliverables via secure portal',
    ],
    badge: '100% Yours',
  },
  {
    id: 'infra',
    title: 'Secure Infrastructure',
    icon: 'ri-server-line',
    description: 'Enterprise-grade tooling and environments that match or exceed your internal security posture.',
    details: [
      'Multi-factor authentication on all client-facing systems',
      'Role-based access control (RBAC) with least-privilege',
      'Real-time security monitoring and anomaly detection',
      'Automated vulnerability scanning on all endpoints',
      'SOC 2 Type II audited cloud infrastructure',
    ],
    badge: 'Enterprise',
  },
  {
    id: 'process',
    title: 'Secure Development',
    icon: 'ri-code-box-line',
    description: 'Code is reviewed, scanned, and delivered through secure channels with full traceability.',
    details: [
      'Mandatory peer code review on all commits',
      'Static and dynamic application security testing (SAST/DAST)',
      'Dependency vulnerability scanning before every deployment',
      'Signed commits and immutable audit logs',
      'Secure CI/CD pipelines with secrets management',
    ],
    badge: 'DevSecOps',
  },
];

const certifications = [
  { name: 'ISO 27001', icon: 'ri-shield-check-line', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  { name: 'SOC 2 Type II', icon: 'ri-file-list-3-line', color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { name: 'GDPR Ready', icon: 'ri-earth-line', color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
  { name: 'HIPAA Compliant', icon: 'ri-hospital-line', color: 'bg-teal-50 border-teal-200 text-teal-700' },
  { name: 'CCPA Aligned', icon: 'ri-user-follow-line', color: 'bg-amber-50 border-amber-200 text-amber-700' },
];

export default function TrustSecurity() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.05 });
  const [expandedId, setExpandedId] = useState<string | null>('compliance');

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section className="py-16 md:py-24 bg-gray-50 relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-primary-500/5 rounded-full blur-3xl -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-3xl translate-y-1/3"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-shield-check-line text-primary-600"></i>
            <span className="text-sm font-medium text-gray-700">Trust & Security</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Your Data, Your IP,
            <br />
            <span className="text-primary-600">Fully Protected</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            We do not just hire great engineers. We protect your code, your data, and your competitive advantage with enterprise-grade security at every step.
          </motion.p>
        </motion.div>

        {/* Certifications Badge Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.35, ...springTransition }}
          className="flex flex-wrap justify-center gap-3 md:gap-4 mb-12 md:mb-16"
        >
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.name}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.4 + index * 0.06, ...springTransition }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-semibold ${cert.color} hover:shadow-md transition-shadow duration-300`}
            >
              <i className={`${cert.icon} text-base`}></i>
              <span>{cert.name}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Main Content: Expandable Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          {securityMeasures.map((measure, index) => {
            const isExpanded = expandedId === measure.id;
            return (
              <motion.div
                key={measure.id}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ ...springTransition, duration: 0.6, delay: index * 0.08 }}
                className={`bg-white rounded-2xl border transition-all duration-500 cursor-pointer overflow-hidden ${
                  isExpanded
                    ? 'border-primary-300 shadow-lg shadow-primary-500/5'
                    : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
                }`}
                onClick={() => toggleExpand(measure.id)}
              >
                <div className="p-5 md:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 md:gap-4">
                      <div className={`w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-xl transition-colors duration-300 flex-shrink-0 ${
                        isExpanded ? 'bg-primary-500' : 'bg-primary-50'
                      }`}>
                        <i className={`${measure.icon} text-xl ${isExpanded ? 'text-white' : 'text-primary-600'}`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base md:text-lg font-bold text-navy-900">{measure.title}</h3>
                          {measure.badge && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] md:text-xs font-bold bg-primary-50 text-primary-700 border border-primary-200">
                              {measure.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-1 leading-relaxed">{measure.description}</p>
                      </div>
                    </div>
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full border transition-all duration-300 flex-shrink-0 ${
                      isExpanded
                        ? 'bg-primary-500 border-primary-500 rotate-180'
                        : 'bg-white border-gray-200'
                    }`}
                    >
                      <i className={`ri-arrow-down-s-line text-lg ${isExpanded ? 'text-white' : 'text-gray-400'}`}></i>
                    </div>
                  </div>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-5 pt-5 border-t border-gray-100">
                          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                            What this means for you
                          </p>
                          <ul className="space-y-2.5">
                            {measure.details.map((detail, i) => (
                              <motion.li
                                key={i}
                                initial={{ opacity: 0, x: -15 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.06 }}
                                className="flex items-start gap-2.5"
                              >
                                <div className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-50 flex-shrink-0 mt-0.5">
                                  <i className="ri-check-line text-xs text-emerald-600"></i>
                                </div>
                                <span className="text-sm text-gray-700">{detail}</span>
                              </motion.li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Trust Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7, delay: 0.6 }}
          className="mt-12 md:mt-16 bg-white rounded-2xl border border-gray-100 overflow-hidden"
        >
          <div className="p-5 md:p-8 border-b border-gray-100">
            <h3 className="text-lg md:text-xl font-bold text-navy-900 text-center mb-1">
              Security You Would Expect From an In-House Team
            </h3>
            <p className="text-sm text-gray-500 text-center">
              How our security posture compares to typical internal hiring
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3">
            {[
              {
                title: 'Background Checks',
                us: '4-stage verification with criminal + reference checks',
                typical: 'Standard resume review + 1 reference call',
                icon: 'ri-shield-user-line',
              },
              {
                title: 'NDA & IP Protection',
                us: 'Signed before day one. Work-for-hire in every contract.',
                typical: 'Often missing or added post-start',
                icon: 'ri-file-list-3-line',
              },
              {
                title: 'Data Access Control',
                us: 'Role-based VPN isolation. Least-privilege always.',
                typical: 'Broad repo access until manually restricted',
                icon: 'ri-lock-line',
              },
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.7 + index * 0.1, duration: 0.5 }}
                className={`p-5 md:p-6 ${index < 2 ? 'md:border-r md:border-gray-100' : ''} ${index > 0 ? 'border-t md:border-t-0 border-gray-100' : ''}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <i className={`${item.icon} text-lg text-primary-600`}></i>
                  <h4 className="text-sm font-bold text-navy-900">{item.title}</h4>
                </div>
                <div className="space-y-3">
                  <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                    <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1">Thoughtic Solutions</p>
                    <p className="text-sm text-gray-700">{item.us}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Typical Internal Hiring</p>
                    <p className="text-sm text-gray-500">{item.typical}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9, ...springTransition }}
          className="mt-10 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6"
        >
          <div className="bg-white rounded-xl border border-gray-100 p-4 md:p-5 flex items-center gap-3 w-full sm:w-auto">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 flex-shrink-0">
              <i className="ri-file-download-line text-xl text-primary-600"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-navy-900">Download our Security Whitepaper</p>
              <p className="text-xs text-gray-500">Full breakdown of our InfoSec policies</p>
            </div>
          </div>

          <a href="#cta" className="bg-navy-900 rounded-xl p-4 md:p-5 flex items-center gap-3 w-full sm:w-auto cursor-pointer hover:bg-navy-800 transition-colors group no-underline">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 flex-shrink-0">
              <i className="ri-shield-check-line text-xl text-primary-400"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-white">Schedule a Security Review</p>
              <p className="text-xs text-gray-300">Walk through our controls with our CISO</p>
            </div>
            <i className="ri-arrow-right-line text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all ml-2"></i>
          </a>
        </motion.div>
      </div>
    </section>
  );
}